import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { Menu } from '@headlessui/react';

import SubMenu from './subMenu';
import { addSlash, getCurrentNodeByLocale, getHardcodedPathByLocaleCode } from '../utils';
import { classNames, getWindowSize } from '../utils';
import LanguageSwitcher from './blocks/languageSwitcher';

const MenuBar = ({ siblingsPages, currentLocaleCode }) => {
  const data = useStaticQuery(staticQuery);
  const [openLanguageSwitcher, setOpenLanguageSwitcher] = useState(false);
  const [search, setSearch] = useState('');

  const currentMenu = getCurrentNodeByLocale(data.allContentstackMenu.nodes, currentLocaleCode);
  const filterMenuItems = () => {
    return currentMenu.items.filter((item) => item.text !== 'Coupons');
  };
  const languageSwitcherNumber = currentMenu?.language_switcher?.length ? 1 : 0;
  const menuItems = filterMenuItems();

  const [windowSize, setWindowSize] = useState(getWindowSize().innerWidth);
  const [visibleElements, setVisibleElements] = useState(menuItems.length + languageSwitcherNumber);
  const [notVisibleElements, setOverflowingElements] = useState(0);

  const isBrowser = typeof window !== 'undefined';
  const currentPath = isBrowser ? window.location.pathname : '';

  const getOverflowingElements = (ul) => {
    let visibleElements = 0;
    let notVisibleElements = 0;
    // if ul Width <= 768px add 2 elements to notVisibleElements
    if (!ul)
      return {
        notOverflowingElements: visibleElements,
        overflowingElements: notVisibleElements,
      };
    if (windowSize < 768) {
      visibleElements = 0;
      notVisibleElements = menuItems.length;
    } else if (676 < ul.clientWidth && ul.clientWidth <= 868) {
      visibleElements = menuItems.length - 3;
      notVisibleElements = 3;
    } else if (ul.clientWidth <= 676) {
      visibleElements = 0;
      notVisibleElements = menuItems.length;
    } else {
      visibleElements = menuItems.length;
      notVisibleElements = 0;
    }

    return {
      notOverflowingElements: visibleElements,
      overflowingElements: notVisibleElements,
    };
  };

  const submenu = menuItems.filter(
    (item) => item?.submenu && item.submenu?.length > 0 && currentPath.startsWith(item.url)
  );
  const handleWindowResize = () => {
    // if window expands, add elements to visibleElements
    let { innerWidth } = window;
    let { notOverflowingElements, overflowingElements } = getOverflowingElements(
      document.querySelector('.nav-wrapper > .nav-inner > nav > ul')
    );

    setWindowSize(innerWidth);
    setVisibleElements(notOverflowingElements);
    setOverflowingElements(overflowingElements);
  };

  const handleVisibilityLanguageSwitcher = useCallback(() => {
    setOpenLanguageSwitcher((prev) => !prev);
  }, []);

  const handleCloseLanguageSwitcher = useCallback(
    (prev) => () => setOpenLanguageSwitcher(prev),
    []
  );

  const handleClickMenuButton = useCallback(() => {
    setOpenLanguageSwitcher(false);
  }, []);

  useEffect(() => {
    let { notOverflowingElements, overflowingElements } = getOverflowingElements(
      document.querySelector('.nav-wrapper > .nav-inner > nav > ul')
    );
    if (notOverflowingElements !== visibleElements) {
      setVisibleElements(notOverflowingElements);
      setOverflowingElements(overflowingElements);
    }
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  const setMenuLabel = () => {
    return windowSize < 768 ? currentMenu?.submenu_label_menu : currentMenu?.submenu_label_more;
  };

  return (
    <>
      <div className="nav-wrapper">
        <div className="nav-inner grid grid-cols-12">
          <a className="logo" href={getHardcodedPathByLocaleCode('home', currentLocaleCode)}>
            <img
              src="/logo.png"
              className="cmp-image__image"
              itemProp="contentUrl"
              data-cmp-hook-image="image"
              alt={currentMenu?.logo_alt}
            />
          </a>

          <nav className="col-span-2 col-start-11 row-start-1 sm:row-start-auto md:col-start-auto sm:col-span-11 md:col-span-11">
            <ul className="flex flex-row flex-nowrap">
              {menuItems.map((item, index) => {
                if (index < visibleElements) {
                  return (
                    <li key={`menu-${index}`}>
                      <a
                        href={addSlash(item.url)}
                        title={item.text}
                        className={classNames(
                          'nav-link',
                          currentPath.startsWith(item.url) && 'current'
                        )}
                      >
                        {item.text}
                      </a>
                    </li>
                  );
                } else {
                  return null;
                }
              })}

              {visibleElements === menuItems?.length && currentMenu?.language_switcher?.length ? (
                <li>
                  <LanguageSwitcher
                    siblingsPages={siblingsPages}
                    languageSwitcher={currentMenu.language_switcher}
                    currentLocaleCode={currentMenu?.locale}
                    handleVisibilityLanguageSwitcher={handleVisibilityLanguageSwitcher}
                    handleCloseLanguageSwitcher={handleCloseLanguageSwitcher}
                    openLanguageSwitcher={openLanguageSwitcher}
                    setOpenLanguageSwitcher={setOpenLanguageSwitcher}
                    notVisibleElements={notVisibleElements}
                    languageSwitcherAriaLabel={currentMenu?.language_switcher_arialabel}
                  />
                </li>
              ) : null}

              {notVisibleElements > 0 && (
                <Menu as="li" className="mobile-menu">
                  {({ open }) => {
                    return (
                      <>
                        <Menu.Button onClick={handleClickMenuButton}>
                          <div className={classNames('mobile-menu-button', open && 'open')}>
                            {setMenuLabel()}
                          </div>
                        </Menu.Button>
                        <Menu.Items className="mobile-menu-items">
                          {menuItems.slice(visibleElements).map((item) => (
                            <Menu.Item>
                              {({ active }) => (
                                <a className={`${active && 'font-bold'}`} href={addSlash(item.url)}>
                                  {item.text}
                                </a>
                              )}
                            </Menu.Item>
                          ))}

                          {currentMenu?.language_switcher?.length ? (
                            <Menu.Item>
                              <LanguageSwitcher
                                siblingsPages={siblingsPages}
                                languageSwitcher={currentMenu.language_switcher}
                                currentLocaleCode={currentMenu?.locale}
                                handleVisibilityLanguageSwitcher={handleVisibilityLanguageSwitcher}
                                handleCloseLanguageSwitcher={handleCloseLanguageSwitcher}
                                openLanguageSwitcher={openLanguageSwitcher}
                                setOpenLanguageSwitcher={setOpenLanguageSwitcher}
                                notVisibleElements={notVisibleElements}
                                languageSwitcherAriaLabel={currentMenu?.language_switcher_arialabel}
                              />
                            </Menu.Item>
                          ) : null}
                        </Menu.Items>
                      </>
                    );
                  }}
                </Menu>
              )}
            </ul>
          </nav>

          <div className="col-span-9 sm:col-span-12 row-start-auto sm:row-start-1 md:row-start-auto md:col-span-3 search-wrapper-main">
            <div className="search-wrapper">
              <div className="newsletter-wrapper">
                <a
                  className="newsletter"
                  href={getHardcodedPathByLocaleCode('subscribe', currentLocaleCode)}
                >
                  <span>{currentMenu?.newsletter_title}</span>
                </a>
              </div>
              <form
                method="get"
                autoComplete="off"
                action={getHardcodedPathByLocaleCode('search', currentLocaleCode)}
              >
                <div className="relative search">
                  <label htmlFor="fulltext" className="hidden">
                    {currentMenu?.search_input_label}
                  </label>
                  <input
                    className="cmp-search__input"
                    type="search"
                    name="fulltext"
                    id="fulltext"
                    placeholder={currentMenu?.search_input_placeholder}
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                    role='search'
                  />
                  {search !== '' ? <button type='button' onClick={() => setSearch('')} className='cmp-search__clear'>x</button> : null}
                  <button type='submit' className="search-icon" aria-label='Search' onSubmit={getHardcodedPathByLocaleCode('search', currentLocaleCode)}>
                    <i
                      className="icon"
                      aria-hidden="true"
                      alt={currentMenu?.search_input_label}
                    ></i>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {submenu.length > 0 && (
        <SubMenu
          pages={submenu[0].submenu}
          currentPath={currentPath}
          submenuLabelMenu={currentMenu?.submenu_label_menu}
          submenuLabelMore={currentMenu?.submenu_label_more}
        />
      )}
    </>
  );
};
export default MenuBar;

// select pages with depth 0 and order them by order
const staticQuery = graphql`
  query {
    allContentstackMenu(
      filter: { domain_name: { eq: "airwick.us" }, locale: { in: ["en-es", "en-us"] } }
    ) {
      nodes {
        items {
          text
          url
          submenu {
            text
            url
          }
        }
        language_switcher {
          locale_code
          title
        }
        language_switcher_arialabel
        logo_alt
        newsletter_title
        search_input_placeholder
        search_input_label
        submenu_label_menu
        submenu_label_more
        locale
      }
    }
  }
`;
